/**
 * @file This file contains the PrivacyPolicy component which renders Essentia Analytic's Privacy Policy.
 */
import React from 'react';
import { PageLayout } from './PageLayout';
import {
    Typography,
    Link,
    List,
    Table,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    TableHead,
    TableBody,
    ListItem
} from '@mui/material';
import { styled } from '@mui/system';
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';
import { CenteredContainer } from './TermsOfUse';

const StyledListItem = styled(ListItem)({
    display: 'list-item'
});

const StyledList = styled(List)({
    listStyleType: 'disc',
    paddingLeft: '2rem',
    paddingTop: '0rem',
    paddingBottom: '0rem'
});

const InlineBold = styled(Box)({
    fontWeight: '600',
    display: 'inline'
});

/**
 * Renders a styled Header text component, with header2 variant typography (as defined in theme.js) with additional weight and padding.
 *
 * @component
 * @param {string} text the string to display with Header styling
 * @returns {React.ReactElement} the Header component
 */
const Header = ({ text }) => {
    return (
        <Typography
            variant='header2'
            style={{ fontWeight: '600' }}
            paddingBottom='1rem'
            paddingTop='1rem'
        >
            {text}
        </Typography>
    );
};

Header.propTypes = {
    text: PropTypes.string.isRequired
};

/**
 * Renders a styled Subheader component with h3 variant typography (as defined in theme.js) with additional weight and padding.
 *
 * @component
 * @param {string} text the string to display with SubHeader styling
 * @returns {React.ReactElement} the SubHeader component
 */
const Subheader = ({ text }) => {
    return (
        <Typography
            variant='h3'
            style={{ fontWeight: '600' }}
            paddingBottom={'0.8rem'}
        >
            {text}
        </Typography>
    );
};

Subheader.propTypes = {
    text: PropTypes.string.isRequired
};

/**
 * Renders a Paragraph typography component in body1 variant (from global theme) and with padding.
 *
 * @component
 * @param {object} children any content to be rendered as a paragraph
 * @returns {React.ReactElement} the Paragraph component
 */
const Paragraph = ({ children }) => {
    return (
        <Typography component='div' variant='body1' paddingBottom='1rem'>
            {children}
        </Typography>
    );
};

Paragraph.propTypes = {
    children: PropTypes.object.isRequired
};

/**
 * Renders a mailto link which opens the user's default mail program and creates an email addressed to the Essentia GDPR contact email address.
 *
 * @component
 * @param {string} label The Mailto link text to display
 * @returns {React.ReactElement} the MailToGDPRLink component
 */
const MailToGDPRLink = ({ label }) => {
    const contactAddress = 'gdpr@essentia-analytics.com';

    return <Link href={'mailto:' + contactAddress}>{label}</Link>;
};

MailToGDPRLink.propTypes = {
    label: PropTypes.string.isRequired
};

/**
 * Renders a link which directs the user to the Essentia Analytics website cookie policy.
 *
 * @component
 * @param {string} label The link's text to display
 * @returns {React.ReactElement} the CookiePolicyLink component
 */
const CookiePolicyLink = ({ label }) => {
    const cookiePolicyAddress =
        'https://www.essentia-analytics.com/essentia-cookie-policy/';

    return <Link href={cookiePolicyAddress}>{label}</Link>;
};

CookiePolicyLink.propTypes = {
    label: PropTypes.string.isRequired
};

const purposesTableData = [
    {
        purpose: 'To register you as a user of our Services',
        types: ['Identity'],
        lawfulBasis: [
            'Performance of a contract',
            'Necessary for our legitimate interests'
        ]
    },
    {
        purpose:
            'To use the personal data inputted by you as part of our Services',
        types: ['Usage'],
        lawfulBasis: [
            'Consent',
            'Performance of a contract',
            'Necessary for our legitimate interests'
        ]
    },
    {
        purpose:
            'To manage our relationship with you which will include: (a) Notifying you about changes to our terms or privacy policy (b) Asking you to leave a review or take a survey',
        types: ['Identity', 'Marketing and Communications'],
        lawfulBasis: [
            'Performance of a contract with you',
            'Necessary to comply with a legal obligation',
            'Necessary for our legitimate interests (to keep our records updated and to study how customers use our products/services)'
        ]
    },
    {
        purpose:
            'To administer and protect our business and this website (including troubleshooting, data analysis, testing, system maintenance, support, reporting and hosting of data)',
        types: ['Identity', 'Technical'],
        lawfulBasis: [
            'Necessary for our legitimate interests (for running our business, provision of administration and IT services, network security, to prevent fraud and in the context of a business reorganisation or group restructuring exercise)',
            'Necessary to comply with a legal obligation'
        ]
    },
    {
        purpose:
            'To deliver relevant website content and newsletters to you and measure or understand the effectiveness of the information we send you',
        types: [
            'Identity',
            'Usage',
            'Marketing and Communications',
            'Technical'
        ],
        lawfulBasis: [
            'Necessary for our legitimate interests (to study how customers use our products/services, to develop them, to grow our business and to inform our marketing strategy)'
        ]
    },
    {
        purpose:
            'To use data analytics to improve our website, products/services, marketing, customer relationships and experiences',
        types: ['Technical', 'Usage'],
        lawfulBasis: [
            'Necessary for our legitimate interests (to define types of customers for our products and services, to keep our website updated and relevant, to develop our business and to inform our marketing strategy)'
        ]
    }
];

/**
 * Renders a table describing the ways Essentia plans to use personal user data, populated with text from the purposesTableData array.
 * Uses MUI Table components with additional styling and layout properties.
 *
 * @component
 * @returns {React.ReactElement} the PurposesTable component displaying a table of user data usage purposes
 */
const PurposesTable = () => {
    return (
        <TableContainer component={Paper} sx={{ marginBottom: '1rem' }}>
            <Table sx={{ minWidth: 600 }}>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <InlineBold>Purpose/Activity</InlineBold>
                        </TableCell>
                        <TableCell>
                            <InlineBold>Type of data</InlineBold>
                        </TableCell>
                        <TableCell>
                            <InlineBold>
                                Lawful basis for processing including basis of
                                legitimate interest
                            </InlineBold>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {purposesTableData.map((row) => (
                        <TableRow key={row.purpose}>
                            <TableCell>{row.purpose}</TableCell>
                            <TableCell>
                                <List sx={{ listStyle: 'lower-alpha', pl: 2 }}>
                                    {row.types.map((i) => (
                                        <StyledListItem key={i}>
                                            {i}
                                        </StyledListItem>
                                    ))}
                                </List>
                            </TableCell>
                            <TableCell>
                                <List sx={{ listStyle: 'lower-alpha', pl: 2 }}>
                                    {row.lawfulBasis.map((i) => (
                                        <StyledListItem key={i}>
                                            {i}
                                        </StyledListItem>
                                    ))}
                                </List>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

/**
 * Renders a page containing Essentia Analytic's Privacy Policy which is split into numbered sections with consistent styling and formatting.
 * Uses the PageLayout component for consistent page toolbar, the Paragraph and Header components (defined in this file) for the majority of the text,
 * as well as the PurposesTable component and various lists and links.
 *
 * @returns {React.ReactElement} The PrivacyPolicy component describing Essentia Analytic's Privacy Policy
 */
const PrivacyPolicy = () => {
    return (
        <PageLayout>
            <CenteredContainer>
                <Typography
                    variant='header0'
                    align='center'
                    paddingBottom='2rem'
                >
                    Privacy Policy
                </Typography>
                <Header text='1. INTRODUCTION' />
                <Paragraph>
                    Welcome to{' '}
                    <InlineBold>ESSENTIA ANALYTICS LIMITED’s</InlineBold>{' '}
                    privacy notice. We respect your privacy and are committed to
                    protecting your personal data.
                </Paragraph>
                <Paragraph>
                    We (&quot;
                    <InlineBold>Essentia</InlineBold>
                    &quot; and/or &quot;
                    <InlineBold>us</InlineBold>
                    &quot; or &quot;
                    <InlineBold>we</InlineBold>
                    &quot;) are a fintech company who provide behavioural
                    software and related services (the &quot;
                    <InlineBold>Services</InlineBold>
                    &quot;) to financial institutions to help their employees
                    improve their investment decision-making. We are a company
                    registered in England and Wales whose registered office is
                    at 1 Mark Square, London, EC2A 4EG, UK with company number
                    071642540.
                </Paragraph>
                <Paragraph>
                    This privacy notice aims to give you information on how we
                    collect and processes personal data through your use of this
                    website and/or or the use by you as an individual of our
                    Services. We are the data controller in respect of this
                    personal data that we collect. This privacy notice will then
                    inform you as to how we look after your personal data and
                    tell you about your privacy rights and how the law protects
                    you.
                </Paragraph>
                <Paragraph>
                    We have appointed a data privacy manager who is responsible
                    for overseeing questions in relation to this privacy notice.
                    If you have any questions about this privacy notice,
                    including any requests to exercise your legal rights, please
                    contact the data privacy manager at{' '}
                    <MailToGDPRLink label='gdpr@essentia-analytics.com' />
                </Paragraph>
                <Paragraph>
                    You have the right to make a complaint at any time to the
                    Information Commissioner’s Office (ICO), the UK supervisory
                    authority for data protection issues (www.ico.org.uk). We
                    would, however, appreciate the chance to deal with your
                    concerns before you approach the ICO so please contact us in
                    the first instance.
                </Paragraph>
                <Header text='2. THE DATA WE COLLECT ABOUT YOU' />
                <Paragraph>
                    Personal data, or personal information, means any
                    information about an individual from which that person can
                    be identified. It does not include data where the identity
                    has been removed (anonymous data).
                </Paragraph>
                <Paragraph>
                    We may collect, use, store and transfer different kinds of
                    personal data about you which we have grouped together
                    follows:
                </Paragraph>
                <Paragraph>
                    <StyledList>
                        <StyledListItem>
                            <InlineBold>Identity Data</InlineBold> includes
                            first name, last name, username or similar
                            identifier, title and email address.
                        </StyledListItem>
                        <StyledListItem>
                            <InlineBold>Technical Data</InlineBold> includes
                            internet protocol (IP) address, your login data,
                            browser type and version, time zone setting and
                            location, browser plug-in types and versions,
                            operating system and platform and other technology
                            on the devices you use to access this website.
                        </StyledListItem>
                        <StyledListItem>
                            <InlineBold>Usage Data</InlineBold> includes
                            information about how you use our website and
                            Services including the submission by you of health
                            and mood related data.
                        </StyledListItem>
                        <StyledListItem>
                            <InlineBold>
                                Marketing and Communications Data
                            </InlineBold>{' '}
                            includes your preferences in receiving marketing
                            from us and your communication preferences.
                        </StyledListItem>
                    </StyledList>
                </Paragraph>
                <Paragraph>
                    We also collect, use and share{' '}
                    <InlineBold>Aggregated Data</InlineBold> such as statistical
                    or demographic data for any purpose. Aggregated Data may be
                    derived from your personal data but is{' '}
                    <InlineBold>not</InlineBold> considered personal data in law
                    as this data does not directly or indirectly reveal your
                    identity. For example, we may aggregate your Usage Data to
                    calculate the percentage of users accessing a specific
                    website feature. However, if we combine or connect
                    Aggregated Data with your personal data so that it can
                    directly or indirectly identify you, we treat the combined
                    data as personal data which will be used in accordance with
                    this privacy notice.
                </Paragraph>
                <Paragraph>
                    In order to perform the Services, we may, with your consent,
                    collect data that may include information relating to your
                    health and mood and certain biometric data. We do not{' '}
                    <InlineBold>otherwise</InlineBold> collect any other Special
                    Categories of Personal Data about you (this includes details
                    about your race or ethnicity, religious or philosophical
                    beliefs, sex life, sexual orientation, political opinions,
                    trade union membership, information about your health and
                    genetic and biometric data).
                </Paragraph>
                <Header text='3. HOW IS YOUR PERSONAL DATA COLLECTED?' />
                <Paragraph>
                    We use different methods to collect data from and about you
                    including through:
                </Paragraph>
                <StyledList>
                    <StyledListItem>
                        <InlineBold>Direct interactions.</InlineBold> You may
                        give us your Identity Data by filling in forms or by
                        corresponding with us by post, phone, email or
                        otherwise.
                    </StyledListItem>
                    <StyledListItem>
                        <InlineBold>
                            Automated technologies or interactions.
                        </InlineBold>{' '}
                        As you interact with our website, we may automatically
                        collect Technical Data about your equipment, browsing
                        actions and patterns. We collect this personal data by
                        using cookies, server logs and other similar
                        technologies. We may also receive Technical Data about
                        you if you visit other websites employing our cookies.
                        Please see our{' '}
                        <CookiePolicyLink label='cookie policy' /> for further
                        details.
                    </StyledListItem>
                    <StyledListItem>
                        <InlineBold>
                            Third parties or publicly available sources.
                        </InlineBold>{' '}
                        We may receive personal data about you from various
                        third parties and public sources as set out below:
                        <List sx={{ listStyle: 'lower-alpha', pl: 4 }}>
                            <StyledListItem>
                                Analytics providers, such as Google based
                                outside the EU
                            </StyledListItem>
                            <StyledListItem>
                                Search information providers, such as LinkedIn
                                and IPREO, based outside the EU
                            </StyledListItem>
                        </List>
                    </StyledListItem>
                </StyledList>
                <Header text='4. HOW WE USE YOUR PERSONAL DATA' />
                <Paragraph>
                    We will only use your personal data when the law allows us
                    to. Most commonly, we will use your personal data in the
                    following circumstances:
                </Paragraph>
                <Paragraph>
                    <StyledList>
                        <StyledListItem>
                            Where we need to perform the contract we are about
                            to enter into or have entered into for the provision
                            of the Services.
                        </StyledListItem>
                        <StyledListItem>
                            Where it is necessary for our legitimate interests
                            (or those of a third party) and your interests and
                            fundamental rights do not override those interests.
                        </StyledListItem>
                        <StyledListItem>
                            Where we need to comply with a legal or regulatory
                            obligation.
                        </StyledListItem>
                    </StyledList>
                </Paragraph>
                <Paragraph>
                    Generally we do not rely on consent as a legal basis for
                    processing your personal data other than in relation to the
                    collection of any health and mood related data from you as
                    part of our Services. You have the right to withdraw consent
                    to marketing at any time by{' '}
                    <MailToGDPRLink label='Contacting us' />.
                </Paragraph>
                <Subheader text='PURPOSES FOR WHICH WE WILL USE YOUR PERSONAL DATA' />
                <Paragraph>
                    We have set out below, in a table format, a description of
                    all the ways we plan to use your personal data, and which of
                    the legal bases we rely on to do so. We have also identified
                    what our legitimate interests are where appropriate.
                </Paragraph>
                <Paragraph>
                    Note that we may process your personal data for more than
                    one lawful ground depending on the specific purpose for
                    which we are using your data. Please{' '}
                    <MailToGDPRLink label='Contact us' /> if you need details
                    about the specific legal ground we are relying on to process
                    your personal data where more than one ground has been set
                    out in the table below.
                </Paragraph>
                <PurposesTable />
                <Subheader text='MARKETING' />
                <Paragraph>
                    You can ask us to stop sending you marketing messages at any
                    time by logging into the website and checking or unchecking
                    relevant boxes to adjust your marketing preferences or by
                    following the opt-out links on any marketing message sent to
                    you or by <MailToGDPRLink label='Contacting us' /> at any
                    time.
                </Paragraph>
                <Subheader text='COOKIES' />
                <Paragraph>
                    You can set your browser to refuse all or some browser
                    cookies, or to alert you when websites set or access
                    cookies. If you disable or refuse cookies, please note that
                    some parts of this website may become inaccessible or not
                    function properly. For more information about the cookies we
                    use, please see the{' '}
                    <CookiePolicyLink label='Essentia Cookie Policy' />.
                </Paragraph>
                <Subheader text={'CHANGE OF PURPOSE'} />
                <Paragraph>
                    We will only use your personal data for the purposes for
                    which we collected it, unless we reasonably consider that we
                    need to use it for another reason and that reason is
                    compatible with the original purpose. If you wish to get an
                    explanation as to how the processing for the new purpose is
                    compatible with the original purpose, please{' '}
                    <MailToGDPRLink label='Contact us' />.
                </Paragraph>
                <Paragraph>
                    If we need to use your personal data for an unrelated
                    purpose, we will notify you and we will explain the legal
                    basis which allows us to do so.
                </Paragraph>
                <Paragraph>
                    Please note that we may process your personal data without
                    your knowledge or consent, in compliance with the above
                    rules, where this is required or permitted by law.
                </Paragraph>
                <Header text='5. DISCLOSURES OF YOUR PERSONAL DATA' />
                <Paragraph>
                    We may have to share your personal data with the parties set
                    out below for the purposes set out in the table in paragraph
                    4 above.
                </Paragraph>
                <Paragraph>
                    <StyledList>
                        <StyledListItem>
                            Professional cloud services and technology providers
                            such as hosting providers, CRM providers and other
                            professional cloud services which allow us to
                            provide our Services.
                        </StyledListItem>
                        <StyledListItem>
                            Third parties to whom we may choose to sell,
                            transfer, or merge parts of our business or our
                            assets. Alternatively, we may seek to acquire other
                            businesses or merge with them. If a change happens
                            to our business, then the new owners may use your
                            personal data in the same way as set out in this
                            privacy notice.
                        </StyledListItem>
                    </StyledList>
                </Paragraph>
                <Paragraph>
                    We require all third parties to respect the security of your
                    personal data and to treat it in accordance with the law. We
                    do not allow our third-party service providers to use your
                    personal data for their own purposes and only permit them to
                    process your personal data for specified purposes and in
                    accordance with our instructions.
                </Paragraph>
                <Header text='6. INTERNATIONAL TRANSFERS' />
                <Paragraph>
                    Some of our Third Parties are based outside the European
                    Economic Area (<InlineBold>EEA</InlineBold>) so their
                    processing of your personal data will involve a transfer of
                    data outside the EEA. Whenever we transfer your personal
                    data out of the EEA, we ensure a similar degree of
                    protection is afforded to it by ensuring at least one of the
                    following safeguards is implemented:
                </Paragraph>
                <StyledList>
                    <StyledListItem>
                        We will only transfer your personal data to countries
                        that have been deemed to provide an adequate level of
                        protection for personal data by the European Commission.
                    </StyledListItem>
                    <StyledListItem>
                        Where we use certain service providers, we may use
                        specific contracts approved by the European Commission
                        which give personal data the same protection it has in
                        Europe.
                    </StyledListItem>
                    <StyledListItem>
                        Where we use providers based in the US, we may transfer
                        data to them if they are certified with the UK Extension
                        to the EU-U.S. Data Privacy Framework or have
                        appropriate safeguards in place, such as the
                        International Data Transfer Agreement, the UK Addendum
                        to the EU Standard Contractual Clauses, or rely on one
                        of the available derogations under Article 49 of the UK
                        GDPR for international data transfers.
                    </StyledListItem>
                    <StyledListItem>
                        Please <MailToGDPRLink label='contact us' /> if you want
                        further information on the specific mechanism used by us
                        when transferring your personal data out of the EEA.
                    </StyledListItem>
                </StyledList>
                <Header text='7. DATA SECURITY' />
                <Paragraph>
                    We have put in place appropriate security measures to
                    prevent your personal data from being accidentally lost,
                    used or accessed in an unauthorised way, altered or
                    disclosed. In addition, we limit access to your personal
                    data to those employees, agents, contractors and other third
                    parties who have a business need to know. They will only
                    process your personal data on our instructions and they are
                    subject to a duty of confidentiality.
                </Paragraph>
                <Paragraph>
                    We have put in place procedures to deal with any suspected
                    personal data breach and will notify you and any applicable
                    regulator of a breach where we are legally required to do
                    so.
                </Paragraph>
                <Header text='8. DATA RETENTION' />
                <Subheader text='HOW LONG WILL YOU USE MY PERSONAL DATA FOR?' />
                <Paragraph>
                    We will only retain your personal data for as long as
                    necessary to fulfil the purposes we collected it for,
                    including for the purposes of satisfying any legal,
                    accounting, or reporting requirements.
                </Paragraph>
                <Paragraph>
                    To determine the appropriate retention period for personal
                    data, we consider the amount, nature, and sensitivity of the
                    personal data, the potential risk of harm from unauthorised
                    use or disclosure of your personal data, the purposes for
                    which we process your personal data and whether we can
                    achieve those purposes through other means, and the
                    applicable legal requirements.
                </Paragraph>
                <Paragraph>
                    By law we have to keep basic information about our customers
                    for six years after they cease being customers. In some
                    circumstances you can ask us to delete your data and please
                    see section 9 below for more information.
                </Paragraph>
                <Paragraph>
                    In some circumstances we may anonymise your personal data
                    (so that it can no longer be associated with you) for
                    research or statistical purposes in which case we may use
                    this information indefinitely without further notice to you.
                </Paragraph>
                <Header text='9. YOUR LEGAL RIGHTS' />
                <Paragraph>
                    Under certain circumstances, you have rights under data
                    protection laws in relation to your personal data. You have
                    the right to:
                </Paragraph>
                <StyledList>
                    <StyledListItem>
                        <InlineBold>Request access</InlineBold> to your personal
                        data (commonly known as a “data subject access
                        request”). This enables you to receive a copy of the
                        personal data we hold about you and to check that we are
                        lawfully processing it.
                    </StyledListItem>
                    <StyledListItem>
                        <InlineBold>Request correction</InlineBold> of the
                        personal data that we hold about you. This enables you
                        to have any incomplete or inaccurate data we hold about
                        you corrected, though we may need to verify the accuracy
                        of the new data you provide to us.
                    </StyledListItem>
                    <StyledListItem>
                        <InlineBold>Request erasure</InlineBold> of your
                        personal data. This enables you to ask us to delete or
                        remove personal data where there is no good reason for
                        us continuing to process it. You also have the right to
                        ask us to delete or remove your personal data where you
                        have successfully exercised your right to object to
                        processing (see below), where we may have processed your
                        information unlawfully or where we are required to erase
                        your personal data to comply with local law. Note,
                        however, that we may not always be able to comply with
                        your request of erasure for specific legal reasons which
                        will be notified to you, if applicable, at the time of
                        your request.
                    </StyledListItem>
                    <StyledListItem>
                        <InlineBold>Object to processing</InlineBold> of your
                        personal data where we are relying on a legitimate
                        interest (or those of a third party) and there is
                        something about your particular situation which makes
                        you want to object to processing on this ground as you
                        feel it impacts on your fundamental rights and freedoms.
                        You also have the right to object where we are
                        processing your personal data for direct marketing
                        purposes. In some cases, we may demonstrate that we have
                        compelling legitimate grounds to process your
                        information which override your rights and freedoms.
                    </StyledListItem>
                    <StyledListItem>
                        <InlineBold>
                            Request restriction of processing
                        </InlineBold>{' '}
                        of your personal data. This enables you to ask us to
                        suspend the processing of your personal data in the
                        following scenarios: (a) if you want us to establish the
                        data’s accuracy; (b) where our use of the data is
                        unlawful but you do not want us to erase it; (c) where
                        you need us to hold the data even if we no longer
                        require it as you need it to establish, exercise or
                        defend legal claims; or (d) you have objected to our use
                        of your data but we need to verify whether we have
                        overriding legitimate grounds to use it.
                    </StyledListItem>
                    <StyledListItem>
                        <InlineBold>Request the transfer</InlineBold> of your
                        personal data to you or to a third party. We will
                        provide to you, or a third party you have chosen, your
                        personal data in a structured, commonly used,
                        machine-readable format. Note that this right only
                        applies to automated information which you initially
                        provided consent for us to use or where we used the
                        information to perform a contract with you.
                    </StyledListItem>
                    <StyledListItem>
                        <InlineBold>Withdraw consent at any time</InlineBold>{' '}
                        where we are relying on consent to process your personal
                        data. However, this will not affect the lawfulness of
                        any processing carried out before you withdraw your
                        consent. If you withdraw your consent, we may not be
                        able to provide certain products or services to you. We
                        will advise you if this is the case at the time you
                        withdraw your consent.
                    </StyledListItem>
                </StyledList>
                <Paragraph>
                    If you wish to exercise any of the rights set out above,
                    please <MailToGDPRLink label='Contact us' />.
                </Paragraph>
                <Subheader text='NO FEE USUALLY REQUIRED' />
                <Paragraph>
                    You will not have to pay a fee to access your personal data
                    (or to exercise any of the other rights). However, we may
                    charge a reasonable fee if your request is clearly
                    unfounded, repetitive or excessive. Alternatively, we may
                    refuse to comply with your request in these circumstances.
                </Paragraph>
                <Subheader text='WHAT WE MAY NEED FROM YOU' />
                <Paragraph>
                    We may need to request specific information from you to help
                    us confirm your identity and ensure your right to access
                    your personal data (or to exercise any of your other
                    rights). This is a security measure to ensure that personal
                    data is not disclosed to any person who has no right to
                    receive it. We may also contact you to ask you for further
                    information in relation to your request to speed up our
                    response.
                </Paragraph>
                <Subheader text='TIME LIMIT TO RESPOND' />
                <Paragraph>
                    We try to respond to all legitimate requests within one
                    month. Occasionally it may take us longer than a month if
                    your request is particularly complex or you have made a
                    number of requests. In this case, we will notify you and
                    keep you updated.
                </Paragraph>
                <Header text='10. GLOSSARY' />
                <Subheader text='LAWFUL BASIS' />
                <Paragraph>
                    <InlineBold>Legitimate Interest</InlineBold> means the
                    interest of our business in conducting and managing our
                    business to enable us to give you the best service/product
                    and the best and most secure experience. We make sure we
                    consider and balance any potential impact on you (both
                    positive and negative) and your rights before we process
                    your personal data for our legitimate interests. We do not
                    use your personal data for activities where our interests
                    are overridden by the impact on you (unless we have your
                    consent or are otherwise required or permitted to by law).
                    You can obtain further information about how we assess our
                    legitimate interests against any potential impact on you in
                    respect of specific activities by{' '}
                    <MailToGDPRLink label='Contacting us' />.
                </Paragraph>
                <Paragraph>
                    <InlineBold>Performance of Contract</InlineBold> means
                    processing your data where it is necessary for the
                    performance of a contract to which you are a party or to
                    take steps at your request before entering into such a
                    contract.
                </Paragraph>
                <Paragraph>
                    <InlineBold>
                        Comply with a legal or regulatory obligation
                    </InlineBold>{' '}
                    means processing your personal data where it is necessary
                    for compliance with a legal or regulatory obligation that we
                    are subject to.
                </Paragraph>
            </CenteredContainer>
        </PageLayout>
    );
};

export default PrivacyPolicy;
